import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	TimkenUnit,
	TimkenVibrationShockLoad,
} from '../../../../components/bearing-search/types/types';
import SelectDropdown from '../../../../components/bearing-search/components/forms/SelectDropdown';
import greaseRelubricateConditionsReducer, {
	GreaseFrequencyConditionsPayload,
	YesNo,
	initialRelubricateConditionsState,
} from './reducer';
import { text, unitMap } from '../../../data';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import {
	getVibrationShockLoad,
	greaseRelubricateLoad,
} from '../../../../components/bearing-search/api/api';
import { SelectOption } from '../../../types/types';
import { getTooltip } from '../../../utils/engineering-tools';

interface RelubricateConditionsProps {
	unit: TimkenUnit;
	isValidated: boolean;
	onChange: ( payload?: GreaseFrequencyConditionsPayload ) => void;
	onSubmit: ( event: FormEvent ) => void;
}

const conditionOptions: SelectOption<YesNo>[] = [
	{
		id: 'yes',
		value: 'Yes',
		label: 'Yes',
	},
	{
		id: 'no',
		value: 'No',
		label: 'No',
	},
];

const RelubricateConditions: React.FC<RelubricateConditionsProps> = ( {
	unit,
	isValidated,
	onChange,
	onSubmit,
} ) => {
	const [ shockLoadOptions, setShockLoadOptions ] = useState<
		TimkenVibrationShockLoad[]
	>( [] );

	const [ formState, dispatch ] = useReducer(
		greaseRelubricateConditionsReducer,
		initialRelubricateConditionsState,
	);

	useEffect( () => {
		// Fetch shock load values
		getVibrationShockLoad().then( ( response ) => {
			const loads = response.shockLoadVibrationDetails;

			if ( loads?.length ) {
				setShockLoadOptions( loads );

				dispatch( {
					type: 'change_number_field',
					payload: [ 'vibrationShockLoad', loads[ 0 ].loadValue ],
				} );
			}
		} );
	}, [] );

	useEffect( () => {
		onChange( formState.requestData );
	}, [ formState.requestData ] );

	const handleFormChange = () => {
		dispatch( {
			type: 'update_request_data',
		} );
	};

	return (
		<form onChange={handleFormChange} onSubmit={onSubmit}>
			<div className="row form-row">
				<div className="col-md-2">
					<NumberInput
						id="grease_relubricate_temperature"
						required={true}
						label={text.temperature}
						unitLabel={unitMap[ unit ].temperature}
						value={formState.temperature}
						min={0}
						max={unit === 'M' ? 176 : 350}
						invalid={isValidated && ! formState.validation.get( 'temperature' )}
						errorMessage={text.errorRequired}
						onChange={( event: ChangeEvent<HTMLInputElement> ) => {
							if (Number(event.target.value) > Number(event.target.max)) {
								event.target.value = event.target.max;
							}
							dispatch({
								type: 'change_number_field',
								payload: ['temperature', event.target.value],
							})
						}
						}
					/>
				</div>

				<div className="col-md-2">
					<NumberInput
						id="grease_relubricate_speed"
						required={true}
						label={text.speed}
						unitLabel="rpm"
						value={formState.rpm}
						min={0}
						max={1000000}
						invalid={isValidated && ! formState.validation.get( 'rpm' )}
						errorMessage={text.errorRequired}
						onChange={( event: ChangeEvent<HTMLInputElement> ) => {
							if (Number(event.target.value) > Number(event.target.max)) {
								event.target.value = event.target.max;
							}
							dispatch({
								type: 'change_number_field',
								payload: ['rpm', event.target.value],
							})
							}
						}
					/>
				</div>

				<div className="col-md-3">
					<SelectDropdown
						id="grease_relubricate_load"
						name="loadDataID"
						required={true}
						label={text.loadCtoPRatio}
						tooltip={getTooltip( text.loadCtoPRatio )}
						value={formState.loadDataID}
						options={greaseRelubricateLoad.map( ( item ) => {
							const { id, name, value } = item;
							return {
								id: `grease_relubricate_load_${ id }`,
								value: id,
								label: name,
							} as SelectOption<number>;
						} )}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_load_id',
								payload: event.target.value as unknown as number,
							} )
						}
					/>
				</div>
			</div>

			<div className="row form-row">
				<div className="col-md-2">
					<SelectDropdown
						id="grease_relubricate_vertical_shaft"
						required={true}
						label={text.verticalShaft}
						value={formState.verticalShaftIdYesNo}
						options={conditionOptions.map( ( item ) => ( {
							...item,
							id: `vertical_shaft_${ item.id }`,
						} ) )}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_boolean_field',
								payload: [ 'verticalShaftIdYesNo', event.target.value as YesNo ],
							} )
						}
					/>
				</div>

				<div className="col-md-2">
					<SelectDropdown
						id="grease_relubricate_oscillating_move"
						required={true}
						label={text.oscillatingMovements}
						value={formState.oscillatingMovementsIdYesNo}
						tooltip={getTooltip( text.oscillatingMovements )}
						options={conditionOptions.map( ( item ) => ( {
							...item,
							id: `oscillating_move_${ item.id }`,
						} ) )}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_boolean_field',
								payload: [
									'oscillatingMovementsIdYesNo',
									event.target.value as YesNo,
								],
							} )
						}
					/>
				</div>

				<div className="col-md-3">
					<SelectDropdown
						id="grease_relubricate_shock_loads"
						required={true}
						label={text.shockLoads}
						value={formState.shockLoadsIdYesNo}
						options={conditionOptions.map( ( item ) => ( {
							...item,
							id: `shock_loads_${ item.id }`,
						} ) )}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_boolean_field',
								payload: [ 'shockLoadsIdYesNo', event.target.value as YesNo ],
							} )
						}
					/>
				</div>

				{formState.shockLoadsIdYesNo === 'Yes' && (
					<div className="col-md-3">
						<SelectDropdown
							id="grease_relubricate_shock_load_value"
							required={true}
							label={text.loadLevel}
							value={formState.vibrationShockLoad}
							options={shockLoadOptions.map(
								( item ) =>
									( {
										id: `shock_load_${ item.id }`,
										label: item.loadName,
										value: item.loadValue,
									} as SelectOption<number> ),
							)}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'vibrationShockLoad', event.target.value ],
								} )
							}
						/>
					</div>
				)}
			</div>

			<input type="submit" hidden />
		</form>
	);
};

export default RelubricateConditions;
